import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import style from './Contact.module.css'
import LocationIcon2 from '../../../assets/svgs/Location2'
import EnvelopeIcon2 from '../../../assets/svgs/Envelope2'

const FloatInput = ({ label, name, type = 'text', value, onChange, required }) => (
  <div className={style.floatGroup}>
    <input
      id={name}
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      className={style.floatInput}
      placeholder=" "
      required={required}
      autoComplete="off"
    />
    <label htmlFor={name} className={style.floatLabel}>{label}</label>
  </div>
)

const FloatTextarea = ({ label, name, value, onChange, required }) => (
  <div className={style.floatGroup}>
    <textarea
      id={name}
      name={name}
      value={value}
      onChange={onChange}
      className={`${style.floatInput} ${style.floatTextarea}`}
      placeholder=" "
      required={required}
      rows={5}
    />
    <label htmlFor={name} className={`${style.floatLabel} ${style.floatLabelArea}`}>{label}</label>
  </div>
)

const Contact = () => {
  const url = process.env.REACT_APP_API_URL || ''
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' })
  const [status, setStatus] = useState(null)

  const handleChange = e => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }))

  const handleSubmit = async e => {
    e.preventDefault()
    setStatus('sending')
    try {
      const res = await fetch(`${url}/users/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      })
      const data = await res.json()
      if (res.ok) {
        setStatus('success')
        setForm({ name: '', email: '', subject: '', message: '' })
      } else {
        console.error('Server error:', data)
        setStatus('error')
      }
    } catch (err) {
      console.error('Network error:', err)
      setStatus('error')
    }
  }

  return (
    <section className={style.section} id="contact">
      <motion.div
        className={style.header}
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.55 }}
      >
        <span className={style.eyebrow}>Contact</span>
        <h2 className={style.title}>Get In Touch</h2>
        <p className={style.subtitle}>
          Have a question or want to work together? We'd love to hear from you.
        </p>
      </motion.div>

      <motion.div
        className={style.card}
        initial={{ opacity: 0, y: 36 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.65, delay: 0.1 }}
      >
        {/* Left info panel */}
        <div className={style.leftPanel}>
          <div className={style.orb1} />
          <div className={style.orb2} />

          <div className={style.panelContent}>
            <h3 className={style.panelTitle}>Let's Connect</h3>
            <p className={style.panelSubtitle}>
              Reach out and we'll get back to you as soon as possible.
            </p>

            <iframe
              title="Chimp office location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2031.9505369503488!2d17.949784012754318!3d59.38385250654948!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x465f9e64eaf49dbb%3A0xe959012e04fe82cf!2sForskningsringen%2C%20Sundbyberg!5e0!3m2!1ssv!2sse!4v1777813900586!5m2!1ssv!2sse"
              className={style.mapIframe}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />

            <div className={style.infoList}>
              <div className={style.infoItem}>
                <div className={style.infoIcon}><LocationIcon2 /></div>
                <div>
                  <p className={style.infoLabel}>Location</p>
                  <p className={style.infoValue}>Forskningsringen 88, 174 62 Sundbyberg</p>
                </div>
              </div>
              <div className={style.infoItem}>
                <div className={style.infoIcon}><EnvelopeIcon2 /></div>
                <div>
                  <p className={style.infoLabel}>Email</p>
                  <p className={style.infoValue}>adchimp.ai@gmail.com</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right form panel */}
        <div className={style.rightPanel}>
          <h3 className={style.formTitle}>Send us a message</h3>
          <form onSubmit={handleSubmit} className={style.form}>
            <div className={style.row}>
              <FloatInput label="Name" name="name" value={form.name} onChange={handleChange} required />
              <FloatInput label="Email" name="email" type="email" value={form.email} onChange={handleChange} required />
            </div>
            <FloatInput label="Subject" name="subject" value={form.subject} onChange={handleChange} required />
            <FloatTextarea label="Message" name="message" value={form.message} onChange={handleChange} required />

            <AnimatePresence>
              {status === 'success' && (
                <motion.div
                  className={style.successMsg}
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                >
                  Message sent successfully! We'll be in touch soon.
                </motion.div>
              )}
              {status === 'error' && (
                <motion.div
                  className={style.errorMsg}
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                >
                  Something went wrong. Please try again.
                </motion.div>
              )}
            </AnimatePresence>

            <motion.button
              type="submit"
              className={style.submitBtn}
              disabled={status === 'sending'}
              whileHover={{ scale: status !== 'sending' ? 1.015 : 1 }}
              whileTap={{ scale: status !== 'sending' ? 0.985 : 1 }}
            >
              {status === 'sending'
                ? <span className={style.spinner} />
                : 'Send Message →'
              }
            </motion.button>
          </form>
        </div>
      </motion.div>
    </section>
  )
}

export default Contact
